import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, ClipboardList, MessageCircle, User, Calendar, FlaskConical,
  Stethoscope, Reply, Plus, RotateCcw, Check, AlertTriangle, Send, Sparkles, Brain,
  Trash2, FileDown, FileText, Activity, ChevronDown, ChevronUp, RefreshCcw, ListPlus,
  Upload, Paperclip, ImageIcon, Lock, GitBranch, X, Loader2, TrendingUp, TrendingDown, Minus, Edit3,
} from "lucide-react";

const STATUS_MAP = {
  aperto:               { cls: "bg-amber-500",   text: "text-amber-700",   label: "Aperto" },
  in_gestione:          { cls: "bg-blue-500",    text: "text-blue-700",    label: "In gestione" },
  inviato_specialista:  { cls: "bg-indigo-500",  text: "text-indigo-700",  label: "Inviato allo specialista" },
  in_attesa:            { cls: "bg-purple-500",  text: "text-purple-700",  label: "In attesa di risposta" },
  risposta_ricevuta:    { cls: "bg-teal-500",    text: "text-teal-700",    label: "Risposta ricevuta" },
  chiuso:               { cls: "bg-emerald-500", text: "text-emerald-700", label: "Chiuso" },
};

const REASON_LABELS = {
  farmaci: "Farmaci / prescrizione", esami: "Esami / accertamenti",
  sintomo: "Sintomo o problema", controllo: "Controllo",
  follow_up: "Follow-up", altro: "Altro",
};

const MODULE_LABELS = {
  vertigini: "Vertigini", dolore_lombare: "Dolore lombare",
  cefalea: "Cefalea", dolore_toracico: "Dolore toracico", dispnea: "Dispnea",
  tosse_persistente: "Tosse persistente",
  dolore_addominale: "Dolore addominale",
  febbre: "Febbre",
  palpitazioni: "Palpitazioni",
  astenia: "Astenia",
  ansia_panico: "Ansia / attacchi di panico",
  disturbi_visivi: "Disturbi visivi",
};

const FOLLOWUP_KIND_LABELS = {
  specialist_response: "Valutazione specialista",
  exam_result: "Esito esami",
  symptoms_change: "Variazione sintomi",
  follow_up: "Follow-up visita",
  new_exams: "Nuovi esami",
  note: "Nota clinica",
};

const TIMELINE_ICONS = {
  case_created:      { icon: ClipboardList, cls: "bg-slate-100 text-slate-600" },
  pathway_chosen:    { icon: Stethoscope,   cls: "bg-blue-100 text-blue-600" },
  sinapsi_sent:      { icon: Send,          cls: "bg-indigo-100 text-indigo-600" },
  followup_specialist_response: { icon: Reply, cls: "bg-teal-100 text-teal-700" },
  followup_new_exams:{ icon: FlaskConical,  cls: "bg-amber-100 text-amber-700" },
  followup_exam_result:{ icon: FlaskConical,cls: "bg-amber-100 text-amber-700" },
  followup_symptoms_change:{ icon: AlertTriangle, cls: "bg-rose-100 text-rose-700" },
  followup_follow_up:{ icon: Calendar,      cls: "bg-sky-100 text-sky-600" },
  followup_note:     { icon: FileText,      cls: "bg-slate-100 text-slate-600" },
  thread_completata: { icon: Check,         cls: "bg-emerald-100 text-emerald-700" },
  thread_rifiutata:  { icon: AlertTriangle, cls: "bg-red-100 text-red-700" },
  case_reopened:     { icon: RotateCcw,     cls: "bg-orange-100 text-orange-700" },
  case_closed:       { icon: Check,         cls: "bg-emerald-100 text-emerald-700" },
  hypothesis_initial:{ icon: Brain,         cls: "bg-purple-100 text-purple-700" },
  hypothesis_finalized:{ icon: Lock,        cls: "bg-emerald-100 text-emerald-800" },
};

// Filtro timeline: teniamo solo eventi chiave (no case_updated/note per ridurre rumore)
const TIMELINE_KEEP = new Set(Object.keys(TIMELINE_ICONS));

const FOLLOW_KINDS = [
  { code: "specialist_response", label: "Risposta / valutazione specialista", cls: "bg-teal-600 hover:bg-teal-700" },
  { code: "exam_result",         label: "Esito esami",                        cls: "bg-amber-600 hover:bg-amber-700" },
  { code: "follow_up",           label: "Follow-up visita",                   cls: "bg-sky-600 hover:bg-sky-700" },
  { code: "new_exams",           label: "Nuovi esami richiesti",              cls: "bg-purple-600 hover:bg-purple-700" },
  { code: "note",                label: "Nota clinica",                       cls: "bg-slate-600 hover:bg-slate-700" },
];

const fmt = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("it-IT",
    { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function CasoDetailPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [followKind, setFollowKind] = useState("specialist_response");
  const [followText, setFollowText] = useState("");
  const [followAttachment, setFollowAttachment] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [dxBusy, setDxBusy] = useState(false);
  const [dxItems, setDxItems] = useState([]);
  const [classifyBusy, setClassifyBusy] = useState(false);

  // Nuova UI a tab: 'summary' | 'updates' | null
  const [activeTab, setActiveTab] = useState(null);
  const [narrBusy, setNarrBusy] = useState(false);
  const [narr, setNarr] = useState(null);
  // Traccia il timestamp dell'ultima rigenerazione della narrativa AI. Quando
  // il medico aggiunge un follow-up il backend rilancia la generazione in
  // background: qui polliamo finché non appare un timestamp più recente per
  // mostrare "Referto aggiornato · ora" senza che l'utente debba ricaricare.
  const [narrRefreshing, setNarrRefreshing] = useState(false);
  const [narrAt, setNarrAt] = useState(null);
  // Sub-aggiornamenti nel tab "updates"
  const [uSymptoms, setUSymptoms] = useState("");
  const [uSymptomsAtt, setUSymptomsAtt] = useState([]);  // array di file objects
  const [uSpecialist, setUSpecialist] = useState("");
  const [uSpecialistAtt, setUSpecialistAtt] = useState([]);
  const [uExamResult, setUExamResult] = useState("");
  const [uExamResultAtt, setUExamResultAtt] = useState([]);
  const [uReinterpretation, setUReinterpretation] = useState("");
  const [uSubBusy, setUSubBusy] = useState(false);
  // Ipotesi finale (blocca dopo reinterpretazione)
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const [finalizeText, setFinalizeText] = useState("");
  const [finalizeNote, setFinalizeNote] = useState("");
  // Come procedere post-update
  const [postPathway, setPostPathway] = useState(null); // 'manage' | 'specialist' | null
  const [postExams, setPostExams] = useState([]);
  const [postRx, setPostRx] = useState([]);
  const [postTherapy, setPostTherapy] = useState("");
  const [postAiExamsBusy, setPostAiExamsBusy] = useState(false);
  const [postAiRxBusy, setPostAiRxBusy] = useState(false);

  // ---- AI Reinterpretazione caso ----
  const [reintBusy, setReintBusy] = useState(false);
  const [reintData, setReintData] = useState(null);
  const [reintExamsBusy, setReintExamsBusy] = useState(false);
  const [reintExams, setReintExams] = useState([]);
  // Chat medico ↔ AI durante la reinterpretazione
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);

  const runReinterpret = async () => {
    setReintBusy(true); setReintData(null);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-reinterpret`, { max_items: 3 });
      setReintData(data.reinterpretation || null);
      toast.success("Analisi AI aggiornata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setReintBusy(false); }
  };

  const runReinterpretExams = async () => {
    setReintExamsBusy(true); setReintExams([]);
    try {
      const alreadyDone = [
        ...(c.exam_requests || []).map((e) => e.text || e.name || ""),
        ...(c.follow_ups || [])
          .filter((f) => f.kind === "exam_result" || f.kind === "new_exams")
          .map((f) => f.text || ""),
      ].filter(Boolean);
      const hyps = (reintData?.differentials || []).map((d) => d.diagnosis).filter(Boolean);
      const { data } = await api.post(`/cases/${caseId}/ai-suggest-exams`, {
        max_items: 6, hypotheses: hyps, already_done: alreadyDone,
      });
      setReintExams(data.suggestions || []);
      toast.success(`${(data.suggestions || []).length} nuovi esami suggeriti`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setReintExamsBusy(false); }
  };

  const addReintExamsToNew = () => {
    if (reintExams.length === 0) return;
    const text = reintExams.map((s) =>
      s.priority === "urgente"
        ? `[URGENTE] ${s.name}${s.rationale ? ` — ${s.rationale}` : ""}`
        : `${s.name}${s.rationale ? ` — ${s.rationale}` : ""}`
    ).join("\n");
    setFollowKind("new_exams");
    setFollowText((prev) => (prev ? prev + "\n" + text : text));
    setReintExams([]);
    toast.success("Nuovi esami trasferiti nel campo 'Nuovi esami richiesti'");
  };

  const sendChatMessage = async () => {
    const q = chatInput.trim();
    if (!q) return;
    const nextHist = [...chatMessages, { role: "user", content: q }];
    setChatMessages(nextHist); setChatInput(""); setChatBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-chat`, {
        question: q, history: chatMessages,
      });
      setChatMessages([...nextHist, { role: "assistant", content: data.answer || "" }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
      setChatMessages(chatMessages);   // rollback
    } finally { setChatBusy(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}`);
      setC(data.case || null);
    } catch { toast.error("Impossibile caricare il caso"); setC(false); }
  };
  useEffect(() => { load(); }, [caseId]);

  const doAction = async (action, extra = {}) => {
    try {
      await api.post(`/cases/${caseId}/action`, { action, ...extra });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore azione");
    }
  };

  const addFollowUp = async () => {
    if (!followText.trim()) { toast.error("Inserisci il testo della voce"); return; }
    setSaving(true);
    try {
      const { data: resp } = await api.post(`/cases/${caseId}/follow-up`, {
        kind: followKind, text: followText.trim(),
        attachment_url: followAttachment.trim(),
      });
      toast.success("Voce aggiunta al caso");
      setFollowText(""); setFollowAttachment("");
      if (resp?.narrative_refresh) {
        setNarrRefreshing(true);
        pollNarrativeRefresh();
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiunta");
    } finally { setSaving(false); }
  };

  // Polling della cache narrativa AI: chiama /cases/{id} finché non torna un
  // `ai_narrative_at` più recente rispetto a quando abbiamo iniziato ad
  // aspettare. Max 45s (15 tentativi × 3s).
  const pollNarrativeRefresh = () => {
    const startedAt = narrAt || null;
    let tries = 0;
    const stopAt = 15;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/cases/${caseId}`);
        const cc = data?.case || {};
        const t = cc.ai_narrative_at;
        if (t && t !== startedAt) {
          setNarrAt(t);
          setNarrRefreshing(false);
          if (cc.ai_narrative) setNarr(cc.ai_narrative);
          clearInterval(iv);
          toast.success("Referto aggiornato dall'AI");
        }
      } catch { /* silenzioso */ }
      if (tries >= stopAt) {
        clearInterval(iv);
        setNarrRefreshing(false);
      }
    }, 3000);
  };

  const classifySpecialty = async () => {
    setClassifyBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-classify-specialty`, { force: true });
      toast.success(`Classificato: ${data.classification?.specialization || "—"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setClassifyBusy(false); }
  };

  const openSummary = async () => {
    setActiveTab(activeTab === 'summary' ? null : 'summary');
    if (narr || activeTab === 'summary') return;
    setNarrBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-narrative`, {});
      setNarr(data.narrative || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setNarrBusy(false); }
  };

  const submitUpdate = async (kind, text, attachments) => {
    if (!text.trim()) return null;
    setUSubBusy(true);
    try {
      const { data: resp } = await api.post(`/cases/${caseId}/follow-up`,
        { kind, text: text.trim(),
          attachment_url: "",
          attachments: attachments || [] });
      if (resp?.narrative_refresh) {
        setNarrRefreshing(true);
        pollNarrativeRefresh();
      }
      await load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
      return false;
    } finally { setUSubBusy(false); }
  };

  const submitAllUpdates = async () => {
    let done = 0;
    if (uSymptoms.trim()) {
      if (await submitUpdate("symptoms_change", uSymptoms, uSymptomsAtt)) {
        setUSymptoms(""); setUSymptomsAtt([]); done++;
      }
    }
    if (uSpecialist.trim()) {
      if (await submitUpdate("specialist_response", uSpecialist, uSpecialistAtt)) {
        setUSpecialist(""); setUSpecialistAtt([]); done++;
      }
    }
    if (uExamResult.trim()) {
      if (await submitUpdate("exam_result", uExamResult, uExamResultAtt)) {
        setUExamResult(""); setUExamResultAtt([]); done++;
      }
    }
    if (uReinterpretation.trim()) {
      try {
        await api.put(`/cases/${caseId}`, {
          selected_hypothesis: {
            diagnosis: uReinterpretation.trim(), source: "mmg",
            rationale: "Reinterpretazione dopo aggiornamento", stage: "working",
          }
        });
        await submitUpdate("note", `Reinterpretazione diagnostica: ${uReinterpretation.trim()}`, []);
        setUReinterpretation(""); done++;
        await load();
      } catch {}
    }
    if (done > 0) toast.success(`${done} aggiornamento/i salvati`);
    else toast.info("Nessun campo compilato");
  };

  const finalizeHypothesis = async () => {
    const txt = (finalizeText || uReinterpretation || c?.selected_hypothesis?.diagnosis || "").trim();
    if (!txt) { toast.error("Inserisci la diagnosi da bloccare come finale"); return; }
    setFinalizeBusy(true);
    try {
      await api.post(`/cases/${caseId}/finalize-hypothesis`, {
        diagnosis: txt,
        source: "mmg",
        note: finalizeNote.trim() || "",
        rationale: "Ipotesi finale dopo aggiornamento del quadro clinico",
      });
      toast.success("Ipotesi finale bloccata sul caso");
      setFinalizeText(""); setFinalizeNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore blocco ipotesi");
    } finally { setFinalizeBusy(false); }
  };

  const runPostAiExams = async () => {
    setPostAiExamsBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-suggest-exams`, { max_items: 6 });
      const items = (data.suggestions || []).map(s => ({ text: s.name, rationale: s.rationale, priority: s.priority }));
      setPostExams(prev => [...prev, ...items]);
      toast.success(`${items.length} esami suggeriti`);
    } catch { toast.error("Errore AI"); }
    finally { setPostAiExamsBusy(false); }
  };
  const runPostAiRx = async () => {
    setPostAiRxBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-suggest-prescription`, { max_items: 5 });
      const items = (data.suggestions || []).map(s => ({
        text: `${s.drug} — ${s.dose}${s.duration ? " × "+s.duration : ""}${s.indication ? " ("+s.indication+")":""}`,
        cautions: s.cautions,
      }));
      setPostRx(prev => [...prev, ...items]);
      toast.success(`${items.length} farmaci suggeriti`);
    } catch { toast.error("Errore AI"); }
    finally { setPostAiRxBusy(false); }
  };

  const finalizePostPathway = async () => {
    if (postPathway === "manage") {
      try {
        await api.put(`/cases/${caseId}`, {
          exam_requests: [...(c.exam_requests || []), ...postExams],
          prescription: [...(c.prescription || []), ...postRx],
          management: { ...(c.management || {}), terapia: postTherapy },
        });
        toast.success("Gestione diretta aggiornata");
        setPostExams([]); setPostRx([]); setPostTherapy(""); setPostPathway(null);
        await load();
      } catch { toast.error("Errore salvataggio"); }
    } else if (postPathway === "specialist") {
      toast.info("Apri percorso Sinapsi da 'Nuova valutazione' - il caso è già collegato");
    }
  };

  const suggestExamsWithAI = async () => {
    setAiBusy(true); setAiSuggestions([]);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-suggest-exams`, { max_items: 8 });
      setAiSuggestions(data.suggestions || []);
      if (!data.suggestions?.length) toast.info("L'AI non ha proposto esami");
      else toast.success(`${data.suggestions.length} esami suggeriti dall'AI`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setAiBusy(false); }
  };

  const useAllAiSuggestions = () => {
    if (aiSuggestions.length === 0) return;
    const text = aiSuggestions.map(s =>
      s.priority === "urgente"
        ? `[URGENTE] ${s.name}${s.rationale ? ` — ${s.rationale}` : ""}`
        : `${s.name}${s.rationale ? ` — ${s.rationale}` : ""}`
    ).join("\n");
    setFollowKind("new_exams");
    setFollowText(prev => (prev ? prev + "\n" + text : text));
    setAiSuggestions([]);
    toast.success("Suggerimenti trasferiti nel campo Nuovi esami");
  };

  const runDifferentialDx = async () => {
    setDxBusy(true); setDxItems([]);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai-differential-diagnosis`, { max_items: 3 });
      setDxItems(data.differentials || []);
      if (!data.differentials?.length) toast.info("L'AI non ha prodotto ipotesi");
      else toast.success(`${data.differentials.length} ipotesi diagnostiche pronte`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setDxBusy(false); }
  };

  if (c === null) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Caricamento…</div>;
  if (c === false) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3">
      <div className="text-slate-500">Caso non trovato o non accessibile.</div>
      <Button onClick={() => navigate("/casi")} variant="outline">Torna ai casi</Button>
    </div>
  );

  const status = STATUS_MAP[c.status] || STATUS_MAP.aperto;
  const isClosed = c.status === "chiuso";
  const timeline = [...(c.timeline || [])]
    .filter(ev => TIMELINE_KEEP.has(ev.kind))
    .sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  const downloadPdf = async () => {
    try {
      const res = await api.post(`/cases/${caseId}/pdf`, {}, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `valutazione-clinica-${(c.patient_last_name||'caso').toLowerCase()}-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore generazione PDF"); }
  };

  const deleteCase = async () => {
    if (!window.confirm(`Cancellare definitivamente il caso di ${c.patient_first_name} ${c.patient_last_name}? L'azione è irreversibile.`)) return;
    try {
      await api.delete(`/cases/${caseId}`);
      toast.success("Caso cancellato");
      navigate("/casi");
    } catch { toast.error("Errore cancellazione"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate("/casi")}
                  data-testid="case-detail-back" className="h-10">
            <ArrowLeft className="h-4 w-4 mr-2" /> Casi clinici
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadPdf}
                    data-testid="case-pdf-btn"
                    className="h-10 border-slate-300">
              <FileDown className="h-4 w-4 mr-2" /> Referto PDF
            </Button>
            {isClosed && (
              <Button variant="outline" onClick={() => doAction("reopen")}
                      data-testid="case-reopen-btn"
                      className="h-10 border-orange-300 text-orange-700 hover:bg-orange-50">
                <RotateCcw className="h-4 w-4 mr-2" /> Riapri caso
              </Button>
            )}
            {!isClosed && (
              <Button variant="outline" onClick={() => doAction("close", { note: "Chiuso dal medico" })}
                      data-testid="case-close-btn"
                      className="h-10 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <Check className="h-4 w-4 mr-2" /> Chiudi caso
              </Button>
            )}
            <Button variant="outline" onClick={deleteCase}
                    data-testid="case-delete-btn"
                    className="h-10 border-red-300 text-red-700 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-2" /> Cancella
            </Button>
          </div>
        </div>

        <Card className="p-5 rounded-2xl border-slate-200 shadow-sm">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                {c.patient_first_name} {c.patient_last_name}
              </h1>
              <div className="text-sm text-slate-500 mt-0.5">
                {c.patient_fiscal_code && <span>CF: {c.patient_fiscal_code} · </span>}
                {c.patient_birth_date && <span>Nato/a il {c.patient_birth_date} · </span>}
                {c.patient_sex && <span>Sesso: {c.patient_sex}</span>}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Badge className={`${status.text} bg-white border-slate-200 text-[12px]`}>
                  <span className={`h-2 w-2 rounded-full ${status.cls} inline-block mr-1.5`} />
                  {status.label}
                </Badge>
                <span className="text-xs text-slate-400">Caso #{c.id?.slice(0, 8)}</span>
                <span className="text-xs text-slate-400">· Creato {fmt(c.created_at)}</span>
              </div>
              {/* Sfera specialistica classificata dall'AI */}
              <div className="mt-2">
                {c.ai_classified_specialty?.specialization ? (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200"
                       data-testid="case-specialty-badge">
                    <Stethoscope className="h-3.5 w-3.5 text-indigo-700" />
                    <span className="text-[12px] text-indigo-800">
                      Rientra nella sfera <b>{c.ai_classified_specialty.specialization}</b>
                      {c.ai_classified_specialty.confidence > 0 && ` (${c.ai_classified_specialty.confidence}%)`}
                    </span>
                    <button onClick={classifySpecialty} disabled={classifyBusy}
                            className="text-[10px] text-indigo-600 hover:text-indigo-900 underline ml-1">
                      {classifyBusy ? "..." : "riclassifica"}
                    </button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={classifySpecialty} disabled={classifyBusy}
                          data-testid="case-classify-btn"
                          className="h-7 border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-[11px]">
                    <Stethoscope className="h-3 w-3 mr-1" />
                    {classifyBusy ? "Classifico…" : "Che specialista serve? (AI)"}
                  </Button>
                )}
              </div>
              {c.ai_classified_specialty?.rationale && (
                <div className="mt-1 text-[11px] text-slate-500 italic max-w-md">
                  {c.ai_classified_specialty.rationale}
                </div>
              )}

              {/* Tab cards: Riepilogo AI + Aggiornamenti caso */}
              <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="case-tab-cards">
                <button onClick={openSummary}
                        data-testid="case-tab-summary"
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition
                          ${activeTab === 'summary' ? "border-fuchsia-500 bg-fuchsia-50" : "border-slate-200 bg-white hover:border-fuchsia-300"}`}>
                  <FileText className="h-4 w-4 text-fuchsia-700" />
                  <span className="text-[12px] font-semibold text-slate-800">Riepilogo clinico</span>
                  {activeTab === 'summary' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                <button onClick={() => setActiveTab(activeTab === 'updates' ? null : 'updates')}
                        data-testid="case-tab-updates"
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition
                          ${activeTab === 'updates' ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
                  <Activity className="h-4 w-4 text-blue-700" />
                  <span className="text-[12px] font-semibold text-slate-800">Aggiornamenti sul caso</span>
                  {activeTab === 'updates' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                <button onClick={() => navigate(`/nuova-valutazione?caseId=${caseId}`)}
                        data-testid="case-edit-initial-btn"
                        className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-slate-200 bg-white hover:border-orange-400 hover:bg-orange-50 transition"
                        title="Correggi anagrafica, motivo visita, sintomi o esame obiettivo">
                  <Edit3 className="h-4 w-4 text-orange-700" />
                  <span className="text-[12px] font-semibold text-slate-800">Modifica dati visita iniziale</span>
                </button>
              </div>
            </div>
            {c.linked_thread_id && (
              <Button size="sm" variant="outline"
                      onClick={() => navigate(`/messaggi/${c.linked_thread_id}`)}
                      data-testid="case-goto-thread"
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                <MessageCircle className="h-4 w-4 mr-1.5" /> Vai al thread
              </Button>
            )}
          </div>
        </Card>

        {/* Red flags */}
        {(c.red_flags || []).length > 0 && (
          <div className="rounded-xl bg-red-50 border-2 border-red-400 p-3" data-testid="case-detail-red-flags">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 mt-0.5 text-red-600 flex-shrink-0" />
              <div>
                <div className="font-bold text-red-800 mb-1">Alert clinici attivi</div>
                <ul className="space-y-0.5 text-sm list-disc pl-5 text-red-700">
                  {c.red_flags.map((f, i) => (<li key={i}>{f.label}</li>))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Motivo + valutazione riepilogo — SOLO se activeTab='summary' */}
        {activeTab === 'summary' && (
        <Card className="p-4 rounded-2xl border-fuchsia-200 bg-fuchsia-50/30 shadow-sm" data-testid="case-summary-panel">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="h-4 w-4 text-fuchsia-700" /> Riepilogo clinico
              </h3>
              {/* Badge di stato del referto AI */}
              {narrRefreshing ? (
                <span data-testid="referto-badge-refreshing"
                      className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  AI sta rielaborando il referto…
                </span>
              ) : (narrAt || c?.ai_narrative_at) ? (
                <span data-testid="referto-badge-updated"
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 border border-emerald-300 px-2 py-0.5 text-[11px] font-semibold text-emerald-800"
                      title={new Date(narrAt || c.ai_narrative_at).toLocaleString('it-IT')}>
                  <Check className="h-3 w-3" />
                  Referto aggiornato · {new Date(narrAt || c.ai_narrative_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}
                </span>
              ) : null}
            </div>
            <Button size="sm" variant="outline" onClick={runDifferentialDx}
                    disabled={dxBusy}
                    data-testid="case-detail-dx-run"
                    className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50">
              <Brain className="h-3.5 w-3.5 mr-1.5" />
              {dxBusy ? "AI…" : "Diagnosi differenziale (AI)"}
            </Button>
          </div>
          {narrBusy && <div className="text-[12px] text-slate-500 italic">AI sta scrivendo il riepilogo…</div>}
          {narr && (
            <div className="mb-3 rounded-lg bg-white border border-fuchsia-200 p-3 space-y-2 text-sm text-slate-800" data-testid="case-summary-narrative">
              {narr.motivo_narrativa && <div><b className="text-fuchsia-800">Motivo: </b>{narr.motivo_narrativa}</div>}
              {narr.sintomi_narrativa && <div><b className="text-fuchsia-800">Sintomatologia: </b>{narr.sintomi_narrativa}</div>}
              {narr.anamnesi_narrativa && <div><b className="text-fuchsia-800">Anamnesi: </b>{narr.anamnesi_narrativa}</div>}
              {narr.esame_obiettivo_narrativa && <div><b className="text-fuchsia-800">E.O.: </b>{narr.esame_obiettivo_narrativa}</div>}
              {narr.valutazione_narrativa && <div><b className="text-fuchsia-800">Valutazione: </b>{narr.valutazione_narrativa}</div>}
              {narr.gravidanza_narrativa && (
                <div className="rounded-md bg-pink-50 border border-pink-200 p-2" data-testid="case-pregnancy-narrative">
                  <b className="text-pink-800">Gravidanza{narr.gravidanza_settimana ? ` · settimana ${narr.gravidanza_settimana}` : ""}: </b>
                  {narr.gravidanza_narrativa}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Info label="Motivi" value={(c.reason_codes || []).map(r => REASON_LABELS[r] || r).join(", ") || "—"} />
            <Info label="Note motivo" value={c.reason_free_text || "—"} />
            {c.symptom_module && (
              <Info label="Modulo sintomo" value={MODULE_LABELS[c.symptom_module] || c.symptom_module.replace(/_/g, " ")} />
            )}
            <Info label="Esame obiettivo" value={c.examination || "—"} />
            <Info label="Valutazione" value={c.clinical_evaluation || "—"} />
            {c.specialist_name && (<Info label="Specialista destinatario" value={c.specialist_name} />)}
          </div>
          {(c.follow_ups || []).length > 0 && (
            <div className="mt-4">
              <div className="font-semibold text-slate-800 mb-1.5 text-sm">Voci aggiunte al caso</div>
              <ul className="space-y-1.5" data-testid="case-followups-list">
                {c.follow_ups.map((fu) => (
                  <li key={fu.id} className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                    <div className="text-[11px] text-slate-500 mb-0.5">
                      <span className="font-semibold uppercase tracking-wide">{FOLLOWUP_KIND_LABELS[fu.kind] || fu.kind.replace(/_/g, " ")}</span>
                      {" · "}{fmt(fu.at)}{" · "}{fu.actor_name}
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{fu.text}</div>
                    {fu.attachment_url && <div className="text-[11px] mt-0.5"><a href={fu.attachment_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">📎 Allegato (link)</a></div>}
                    {Array.isArray(fu.attachments) && fu.attachments.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {fu.attachments.map((a) => (
                          <AttachmentThumb key={a.id} att={a} />
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {dxItems.length > 0 && (
            <DifferentialDxBox items={dxItems} onDismiss={() => setDxItems([])} />
          )}
        </Card>
        )}

        {/* Timeline unificata */}
        <Card className="p-4 rounded-2xl border-slate-200 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-3">Timeline del caso</h3>
          <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4" data-testid="case-timeline">
            {timeline.map((ev) => {
              const iconDef = TIMELINE_ICONS[ev.kind] || TIMELINE_ICONS.case_updated;
              const Icon = iconDef.icon;
              return (
                <li key={ev.id} className="ml-4">
                  <span className={`absolute -left-[13px] mt-0.5 flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-white ${iconDef.cls}`}>
                    <Icon className="h-3 w-3" />
                  </span>
                  <div className="text-[11px] text-slate-500">{fmt(ev.at)} · {ev.actor_name}</div>
                  <div className="text-sm text-slate-800 font-medium">{ev.description}</div>
                  {ev.payload?.note && (
                    <div className="text-[12px] text-slate-600 italic mt-0.5">{ev.payload.note}</div>
                  )}
                  {ev.payload?.text && (
                    <div className="text-[12px] text-slate-600 mt-0.5 whitespace-pre-wrap">{ev.payload.text}</div>
                  )}
                </li>
              );
            })}
            {timeline.length === 0 && (
              <li className="ml-4 text-sm text-slate-500 italic">Nessun evento nella timeline.</li>
            )}
          </ol>
        </Card>

        {/* Aggiornamenti caso — SOLO se activeTab='updates' */}
        {activeTab === 'updates' && (
        <Card className="p-4 rounded-2xl border-blue-200 bg-blue-50/30 shadow-sm" data-testid="case-updates-panel">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-blue-700" />
            <h3 className="font-semibold text-slate-900">Aggiornamenti sul caso clinico</h3>
          </div>

          {/* 3 sub-schede aggiornamento */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Variazione sintomi */}
            <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-3" data-testid="case-update-symptoms">
              <div className="flex items-center gap-1.5 mb-2">
                <AlertTriangle className="h-3.5 w-3.5 text-rose-700" />
                <span className="text-[12px] font-bold text-rose-800">Variazione dei sintomi</span>
              </div>
              <Textarea rows={3} value={uSymptoms} onChange={(e) => setUSymptoms(e.target.value)}
                        placeholder="Es. Comparsa di dispnea; scomparsa della vertigine…"
                        className="text-[12px]"
                        data-testid="update-symptoms-text" />
              <FileUploader caseId={caseId} kind="symptoms_change"
                            files={uSymptomsAtt} setFiles={setUSymptomsAtt}
                            testid="update-symptoms-att" />
            </div>

            {/* Valutazione specialista */}
            <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-3" data-testid="case-update-specialist">
              <div className="flex items-center gap-1.5 mb-2">
                <Reply className="h-3.5 w-3.5 text-teal-700" />
                <span className="text-[12px] font-bold text-teal-800">Valutazione dello specialista</span>
              </div>
              <Textarea rows={3} value={uSpecialist} onChange={(e) => setUSpecialist(e.target.value)}
                        placeholder="Commento/valutazione dello specialista…"
                        className="text-[12px]"
                        data-testid="update-specialist-text" />
              <FileUploader caseId={caseId} kind="specialist_response"
                            files={uSpecialistAtt} setFiles={setUSpecialistAtt}
                            testid="update-specialist-att" />
            </div>

            {/* Esito esami */}
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3" data-testid="case-update-exam">
              <div className="flex items-center gap-1.5 mb-2">
                <FlaskConical className="h-3.5 w-3.5 text-amber-700" />
                <span className="text-[12px] font-bold text-amber-800">Esito esami</span>
              </div>
              <Textarea rows={3} value={uExamResult} onChange={(e) => setUExamResult(e.target.value)}
                        placeholder="Dati principali degli esami eseguiti…"
                        className="text-[12px]"
                        data-testid="update-exam-text" />
              <FileUploader caseId={caseId} kind="exam_result"
                            files={uExamResultAtt} setFiles={setUExamResultAtt}
                            testid="update-exam-att" />
            </div>
          </div>

          {/* Reinterpretazione diagnostica */}
          <div className="mt-3 rounded-xl border border-fuchsia-200 bg-fuchsia-50/40 p-3"
               data-testid="case-update-reinterpret">
            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
              <RefreshCcw className="h-3.5 w-3.5 text-fuchsia-700" />
              <span className="text-[12px] font-bold text-fuchsia-800">Reinterpretazione del caso — aggiornamento diagnosi</span>
            </div>
            <input type="text" value={uReinterpretation} onChange={(e) => setUReinterpretation(e.target.value)}
                   placeholder="Nuova ipotesi diagnostica alla luce degli aggiornamenti"
                   data-testid="update-reinterpret-text"
                   className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" />
            <div className="text-[10px] text-slate-500 italic mt-1 mb-2">
              Il salvataggio aggiorna l'ipotesi di lavoro. Per bloccarla come <b>Ipotesi finale</b> del caso usa il pulsante sotto la timeline.
            </div>

            {/* --- Suggerimento AI Reinterpretazione --- */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={runReinterpret} disabled={reintBusy}
                      data-testid="reint-ai-run"
                      className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-8 text-[12px]">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {reintBusy ? "Rianalisi AI…" : "Suggerimento AI (rianalisi caso)"}
              </Button>
              {reintData && (
                <Button size="sm" variant="outline" onClick={runReinterpretExams} disabled={reintExamsBusy}
                        data-testid="reint-ai-exams"
                        className="border-amber-400 text-amber-800 hover:bg-amber-50 h-8 text-[12px]">
                  <FlaskConical className="h-3.5 w-3.5 mr-1.5" />
                  {reintExamsBusy ? "AI in analisi…" : "Suggerisci nuovi esami (tieni conto di quelli già fatti)"}
                </Button>
              )}
            </div>

            {reintData && (
              <div className="mt-3 rounded-lg border-2 border-fuchsia-300 bg-white p-3"
                   data-testid="reint-ai-panel">
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-fuchsia-700 mb-2">
                  Ipotesi rivalutate dall'AI
                </div>
                <ol className="space-y-2">
                  {(reintData.differentials || []).map((d, i) => {
                    const DeltaIcon = d.delta > 0 ? TrendingUp : d.delta < 0 ? TrendingDown : Minus;
                    const deltaCls = d.delta > 0 ? "text-red-700 bg-red-50" : d.delta < 0 ? "text-emerald-700 bg-emerald-50" : "text-slate-600 bg-slate-50";
                    const sevCls = d.severity === "high" ? "border-red-300" : d.severity === "low" ? "border-emerald-300" : "border-amber-300";
                    return (
                      <li key={i} className={`rounded-lg border-2 ${sevCls} bg-white p-2.5`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-slate-500 font-mono">#{i+1}</span>
                              <span className="font-bold text-slate-900 text-sm">{d.diagnosis}</span>
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${deltaCls}`}>
                                <DeltaIcon className="h-3 w-3" />
                                {d.delta > 0 ? `+${d.delta}` : d.delta}%
                              </span>
                            </div>
                            <div className="mt-1 text-[12px] text-slate-700">{d.rationale}</div>
                          </div>
                          <div className="text-right flex-shrink-0 w-20">
                            <div className="text-xl font-bold text-slate-900 leading-none">{d.probability}%</div>
                            <div className="text-[9px] text-slate-500 uppercase tracking-wide mt-0.5">nuova stima</div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                {reintData.overall_reasoning && (
                  <div className="mt-3 rounded-md bg-fuchsia-50 border border-fuchsia-200 p-2 text-[12px] text-slate-800">
                    <span className="font-bold text-fuchsia-800">Sintesi: </span>{reintData.overall_reasoning}
                  </div>
                )}
                {reintData.suggested_next_step && (
                  <div className="mt-2 text-[11px] text-slate-700 italic">
                    <b>Prossimo passo suggerito:</b> {reintData.suggested_next_step}
                  </div>
                )}
                <div className="text-[10px] text-slate-400 italic mt-2">
                  Assistenza AI. Il medico rimane responsabile della decisione clinica.
                </div>
              </div>
            )}

            {reintExams.length > 0 && (
              <div className="mt-3 rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3"
                   data-testid="reint-ai-exams-panel">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-800">
                    Nuovi esami suggeriti dall'AI
                  </div>
                  <Button size="sm" variant="outline" onClick={addReintExamsToNew}
                          data-testid="reint-add-all-exams"
                          className="border-amber-400 text-amber-800 hover:bg-amber-100 h-7 text-[11px]">
                    <ListPlus className="h-3 w-3 mr-1" /> Trasferisci tutti in "Nuovi esami"
                  </Button>
                </div>
                <ul className="space-y-1.5">
                  {reintExams.map((s, i) => (
                    <li key={i} className="rounded-md bg-white border border-amber-200 p-2 text-[12px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{s.name}</span>
                        {s.priority === "urgente" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">URGENTE</span>
                        )}
                      </div>
                      {s.rationale && <div className="text-[11px] text-slate-600 mt-0.5">{s.rationale}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* --- Chat medico ↔ AI --- */}
            <div className="mt-3 rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-3"
                 data-testid="reint-ai-chat">
              <div className="flex items-center gap-1.5 mb-2">
                <MessageCircle className="h-3.5 w-3.5 text-indigo-700" />
                <span className="text-[12px] font-bold text-indigo-800">Chat con l'AI sul caso</span>
              </div>
              {chatMessages.length > 0 && (
                <div className="max-h-64 overflow-y-auto space-y-2 mb-2 pr-1" data-testid="reint-chat-messages">
                  {chatMessages.map((m, i) => (
                    <div key={i}
                         className={`rounded-lg p-2 text-[12px] ${
                           m.role === "user"
                             ? "bg-slate-100 text-slate-900 border border-slate-200"
                             : "bg-white text-slate-800 border border-indigo-200"
                         }`}>
                      <div className="text-[10px] font-bold uppercase tracking-wide mb-0.5 opacity-70">
                        {m.role === "user" ? "Medico" : "AI"}
                      </div>
                      <div className="whitespace-pre-wrap">{m.content}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter" && !chatBusy) sendChatMessage(); }}
                       placeholder="Es. Alla luce dei nuovi esami, dovrei considerare un consulto cardiologico?"
                       data-testid="reint-chat-input"
                       className="flex-1 h-9 rounded-md border border-indigo-200 bg-white px-3 text-sm" />
                <Button size="sm" onClick={sendChatMessage} disabled={chatBusy || !chatInput.trim()}
                        data-testid="reint-chat-send"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-[12px]">
                  {chatBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Invia</>}
                </Button>
              </div>
              <div className="text-[10px] text-slate-400 italic mt-1">
                L'AI vede tutto il caso e i follow-up. Le risposte sono di supporto: la decisione resta del medico.
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-3">
            <Button onClick={submitAllUpdates} disabled={uSubBusy}
                    data-testid="case-update-submit"
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white">
              <Plus className="h-4 w-4 mr-2" /> {uSubBusy ? "Salvo…" : "Salva aggiornamenti"}
            </Button>
          </div>

          {/* Storico ipotesi diagnostiche */}
          <HypothesisHistory
            initial={c.initial_hypothesis}
            final={c.final_hypothesis}
            history={c.hypothesis_history || []}
          />

          {/* Blocco ipotesi finale */}
          <div className="mt-4 rounded-xl border-2 border-emerald-300 bg-emerald-50/50 p-3"
               data-testid="case-finalize-hypothesis">
            <div className="flex items-center gap-1.5 mb-2">
              <Lock className="h-3.5 w-3.5 text-emerald-800" />
              <span className="text-[12px] font-bold text-emerald-900">
                Ipotesi finale del caso {c.final_hypothesis?.diagnosis ? "— attualmente bloccata" : ""}
              </span>
            </div>
            {c.final_hypothesis?.diagnosis ? (
              <div className="text-[12px] text-emerald-900 mb-2 rounded-md bg-white/60 p-2 border border-emerald-200">
                <b>Diagnosi finale:</b> {c.final_hypothesis.diagnosis}
                {c.final_hypothesis.probability ? ` (${c.final_hypothesis.probability}%)` : ""}
                {c.final_hypothesis.note && <div className="mt-0.5 italic text-[11px]">{c.final_hypothesis.note}</div>}
                <div className="text-[10px] text-emerald-700 mt-0.5">
                  Puoi ri-bloccare una nuova ipotesi in qualsiasi momento (verrà tracciata nella timeline).
                </div>
              </div>
            ) : null}
            <input type="text" value={finalizeText} onChange={(e) => setFinalizeText(e.target.value)}
                   placeholder={c.selected_hypothesis?.diagnosis
                     ? `Es. ${c.selected_hypothesis.diagnosis}`
                     : "Diagnosi da bloccare come finale"}
                   data-testid="finalize-diagnosis-text"
                   className="h-9 w-full rounded-md border border-emerald-200 bg-white px-3 text-sm" />
            <input type="text" value={finalizeNote} onChange={(e) => setFinalizeNote(e.target.value)}
                   placeholder="Nota clinica sintetica (opzionale)"
                   data-testid="finalize-note-text"
                   className="h-8 w-full rounded-md border border-emerald-200 bg-white px-3 mt-2 text-[12px]" />
            <div className="flex justify-end mt-2">
              <Button onClick={finalizeHypothesis} disabled={finalizeBusy}
                      data-testid="finalize-hypothesis-btn"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <Lock className="h-4 w-4 mr-2" />
                {finalizeBusy ? "Blocco…" : "Blocca come ipotesi finale"}
              </Button>
            </div>
          </div>

          {/* Come intendi procedere */}
          <div className="mt-4 pt-3 border-t-2 border-blue-200">
            <div className="text-[12px] font-bold text-slate-800 mb-2">Come intendi procedere (in base a questi aggiornamenti)?</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
              <button onClick={() => setPostPathway(postPathway === "manage" ? null : "manage")}
                      data-testid="case-post-pathway-manage"
                      className={`text-left rounded-xl border-2 p-3 transition
                        ${postPathway === "manage" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-emerald-300"}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <ClipboardList className="h-4 w-4 text-emerald-700" />
                  <span className="font-semibold text-slate-900 text-sm">Gestione diretta</span>
                </div>
                <div className="text-[11px] text-slate-600">Nuovi esami, nuovi farmaci, indicazioni di terapia (con suggerimenti AI)</div>
              </button>
              <button onClick={() => setPostPathway(postPathway === "specialist" ? null : "specialist")}
                      data-testid="case-post-pathway-specialist"
                      className={`text-left rounded-xl border-2 p-3 transition
                        ${postPathway === "specialist" ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Send className="h-4 w-4 text-blue-700" />
                  <span className="font-semibold text-slate-900 text-sm">Gestione specialistica</span>
                </div>
                <div className="text-[11px] text-slate-600">Contatta uno specialista (nuova Sinapsi)</div>
              </button>
            </div>

            {postPathway === "manage" && (
              <div className="rounded-xl border-2 border-emerald-200 bg-white p-3 space-y-3"
                   data-testid="case-post-manage-panel">
                {/* Nuovi esami con AI */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12px] font-bold text-slate-800">Nuovi esami</span>
                    <Button size="sm" variant="outline" onClick={runPostAiExams} disabled={postAiExamsBusy}
                            data-testid="case-post-ai-exams"
                            className="h-7 text-[11px] border-purple-300 text-purple-700">
                      <Sparkles className="h-3 w-3 mr-1" /> {postAiExamsBusy ? "AI…" : "Suggerisci esami (AI)"}
                    </Button>
                  </div>
                  <MiniList lines={postExams} setLines={setPostExams}
                             placeholder="Es. Emocromo, TC torace…" testid="post-exams" />
                </div>
                {/* Prescrizione farmaci con AI */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12px] font-bold text-slate-800">Prescrizione farmaci</span>
                    <Button size="sm" variant="outline" onClick={runPostAiRx} disabled={postAiRxBusy}
                            data-testid="case-post-ai-rx"
                            className="h-7 text-[11px] border-purple-300 text-purple-700">
                      <Sparkles className="h-3 w-3 mr-1" /> {postAiRxBusy ? "AI…" : "Suggerisci prescrizione (AI)"}
                    </Button>
                  </div>
                  <MiniList lines={postRx} setLines={setPostRx}
                             placeholder="Es. Ramipril 5 mg 1 cp ore 8…" testid="post-rx" />
                </div>
                {/* Terapia libera */}
                <div>
                  <span className="text-[12px] font-bold text-slate-800">Terapia / indicazioni al paziente</span>
                  <Textarea rows={2} value={postTherapy} onChange={(e) => setPostTherapy(e.target.value)}
                            placeholder="Es. Riposo, controllo pressorio quotidiano, dieta iposodica…"
                            data-testid="case-post-therapy"
                            className="text-sm mt-1" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={finalizePostPathway} data-testid="case-post-finalize"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Check className="h-4 w-4 mr-2" /> Salva gestione diretta
                  </Button>
                </div>
              </div>
            )}
            {postPathway === "specialist" && (
              <div className="rounded-xl border-2 border-blue-200 bg-white p-3 text-[13px] text-slate-700"
                   data-testid="case-post-specialist-panel">
                Il caso ha già la classificazione AI:
                <b className="text-blue-800">{c.ai_classified_specialty?.specialization || " specializzazione da definire"}</b>.
                Per creare una nuova Sinapsi verso un collega, apri una <b>Nuova valutazione</b> collegata al paziente
                oppure invia dal thread esistente (se presente).
              </div>
            )}
          </div>
        </Card>
        )}

        {/* Aggiungi voci (form legacy) — VISIBILE solo se activeTab='updates' come fallback rapido */}
        {activeTab === 'updates' && (
        <Card className="p-4 rounded-2xl border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-slate-600" />
              <h3 className="font-semibold text-slate-900 text-sm">Aggiungi singola voce (avanzato)</h3>
            </div>
            <Button size="sm" variant="outline" onClick={suggestExamsWithAI}
                    disabled={aiBusy}
                    data-testid="case-detail-ai-suggest"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              {aiBusy ? "AI in analisi…" : "Suggerisci esami con AI"}
            </Button>
          </div>

          {aiSuggestions.length > 0 && (
            <div className="mb-3 rounded-xl border-2 border-purple-200 bg-purple-50/50 p-3"
                 data-testid="case-detail-ai-suggestions">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12px] font-semibold text-purple-800">
                  Proposte AI ({aiSuggestions.length})
                </div>
                <Button size="sm" variant="ghost" onClick={useAllAiSuggestions}
                        data-testid="case-detail-ai-add-all"
                        className="text-purple-700 hover:bg-purple-100 h-7 text-[11px]">
                  Usa tutti come Nuovi esami
                </Button>
              </div>
              <ul className="space-y-1.5">
                {aiSuggestions.map((s, i) => (
                  <li key={i} className="rounded-lg bg-white border border-purple-200 p-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 text-sm">{s.name}</span>
                      {s.priority === "urgente" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">URGENTE</span>
                      )}
                    </div>
                    {s.rationale && (
                      <div className="text-[11px] text-slate-600 mt-0.5">{s.rationale}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-2">
            {FOLLOW_KINDS.map((k) => (
              <button key={k.code} type="button" onClick={() => setFollowKind(k.code)}
                      data-testid={`case-follow-kind-${k.code}`}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border-2 transition
                        ${followKind === k.code ? "border-blue-500 bg-blue-100 text-blue-800" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}>
                {k.label}
              </button>
            ))}
          </div>
          <Label className="text-[12px]">Testo</Label>
          <Textarea rows={4} value={followText} onChange={(e) => setFollowText(e.target.value)}
                    placeholder={followKind === "specialist_response"
                      ? "Trascrivi qui la valutazione dello specialista…"
                      : followKind === "exam_result"
                      ? "Trascrivi i risultati o i dati principali degli esami eseguiti…"
                      : "Descrivi la voce clinica…"}
                    data-testid="case-follow-text" />
          <div className="mt-2">
            <Label className="text-[12px]">Allegato (URL esterno, opzionale)</Label>
            <input type="url" value={followAttachment}
                    onChange={(e) => setFollowAttachment(e.target.value)}
                    placeholder="https:// link esterno (opzionale)"
                    data-testid="case-follow-attachment"
                    className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" />
          </div>
          <div className="flex justify-end mt-3">
            <Button onClick={addFollowUp} disabled={saving || !followText.trim()}
                    data-testid="case-follow-submit"
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white">
              <Plus className="h-4 w-4 mr-2" /> {saving ? "Salvataggio…" : "Aggiungi al caso"}
            </Button>
          </div>
        </Card>
        )}
      </div>
    </div>
  );
}

function MiniList({ lines, setLines, placeholder, testid }) {
  const [txt, setTxt] = useState("");
  const add = () => { if (txt.trim()) { setLines([...lines, { text: txt.trim() }]); setTxt(""); } };
  return (
    <div>
      <div className="flex gap-1.5">
        <input value={txt} onChange={(e) => setTxt(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
               placeholder={placeholder} data-testid={`${testid}-input`}
               className="h-9 flex-1 rounded-md border border-slate-200 bg-white px-3 text-sm" />
        <Button onClick={add} type="button" variant="outline" size="sm"
                data-testid={`${testid}-add`} className="h-9 text-[11px]">
          <ListPlus className="h-3.5 w-3.5 mr-1" /> Aggiungi
        </Button>
      </div>
      {lines.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`${testid}-list`}>
          {lines.map((l, i) => (
            <li key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-slate-50 border border-slate-200 text-[12px]">
              <span className="flex-1 min-w-0">{l.text}</span>
              <button onClick={() => setLines(lines.filter((_, j) => j !== i))}
                      className="text-slate-400 hover:text-red-600 text-xs">×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function FileUploader({ caseId, kind, files, setFiles, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    const uploaded = [];
    for (const file of Array.from(fileList)) {
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`"${file.name}" supera 15 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post(
          `/case-attachments/upload?case_id=${encodeURIComponent(caseId)}&kind=${encodeURIComponent(kind || "")}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } }
        );
        uploaded.push({
          id: data.id, url: data.url, filename: data.filename,
          content_type: data.content_type, size: data.size, is_image: data.is_image,
          kind: data.kind,
        });
      } catch (e) {
        toast.error(`Upload "${file.name}": ${e?.response?.data?.detail || "errore"}`);
      }
    }
    if (uploaded.length) {
      setFiles([...(files || []), ...uploaded]);
      toast.success(`${uploaded.length} file caricato/i`);
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = async (id) => {
    try { await api.delete(`/case-attachments/${id}`); } catch {}
    setFiles((files || []).filter((f) => f.id !== id));
  };

  return (
    <div className="mt-2">
      <input ref={inputRef} type="file" accept="application/pdf,image/*"
             multiple onChange={(e) => handleFiles(e.target.files)}
             className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
              data-testid={`${testid}-btn`}
              className="w-full h-8 rounded-md border border-dashed border-slate-300 bg-white hover:bg-slate-50 flex items-center justify-center gap-1.5 text-[11px] text-slate-600 font-semibold">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
        {busy ? "Caricamento…" : "Carica scansione PDF/immagine"}
      </button>
      {(files || []).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`${testid}-list`}>
          {files.map((f) => (
            <div key={f.id} className="relative">
              <AttachmentThumb att={f} size="sm" />
              <button type="button" onClick={() => removeFile(f.id)}
                      className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                      data-testid={`${testid}-remove-${f.id}`}>
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentThumb({ att, size = "md" }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);
  const isImg = att.is_image ?? (att.content_type || "").startsWith("image/");
  const dim = size === "sm" ? "h-14 w-14" : "h-20 w-20";
  useEffect(() => {
    let alive = true; let created = null;
    if (isImg && att.id) {
      api.get(`/case-attachments/${att.id}`, { responseType: "blob" })
        .then((res) => {
          if (!alive) return;
          created = URL.createObjectURL(res.data);
          setBlobUrl(created);
        })
        .catch(() => alive && setErr(true));
    }
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [att.id, isImg]);
  const openFull = async () => {
    try {
      const res = await api.get(`/case-attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Impossibile aprire il file"); }
  };
  if (isImg && blobUrl) {
    return (
      <button type="button" onClick={openFull}
              className={`${dim} rounded-md overflow-hidden border border-slate-300 hover:border-blue-400 relative group`}
              title={att.filename}
              data-testid={`attach-thumb-${att.id}`}>
        <img src={blobUrl} alt={att.filename} className="w-full h-full object-cover" />
      </button>
    );
  }
  return (
    <button type="button" onClick={openFull}
            className={`${dim} rounded-md border border-slate-300 hover:border-blue-400 bg-slate-50 flex flex-col items-center justify-center gap-0.5 text-slate-600 text-[9px] text-center px-1 leading-tight`}
            title={att.filename}
            data-testid={`attach-thumb-${att.id}`}>
      {isImg ? <ImageIcon className="h-5 w-5" /> : <FileText className="h-5 w-5 text-red-500" />}
      <span className="truncate w-full">{att.filename}</span>
      {err && <span className="text-red-500">!</span>}
    </button>
  );
}

function HypothesisHistory({ initial, final, history }) {
  const items = [];
  const seen = new Set();
  const add = (h, stage) => {
    if (!h || !h.diagnosis) return;
    const key = `${stage}:${h.diagnosis}:${h.at || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ ...h, stage: stage || h.stage || "working" });
  };
  add(initial, "initial");
  (history || []).forEach((h) => add(h, h.stage || "working"));
  add(final, "final");
  if (items.length === 0) return null;
  items.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  const stageMeta = {
    initial:  { label: "Ipotesi iniziale (AI)", cls: "bg-purple-50 border-purple-300 text-purple-800", dot: "bg-purple-500" },
    working:  { label: "Aggiornata (MMG)",      cls: "bg-blue-50 border-blue-300 text-blue-800",       dot: "bg-blue-500" },
    final:    { label: "IPOTESI FINALE",        cls: "bg-emerald-50 border-emerald-400 text-emerald-900", dot: "bg-emerald-600" },
  };
  return (
    <div className="mt-4 rounded-xl border-2 border-slate-200 bg-white p-3"
         data-testid="hypothesis-history-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <GitBranch className="h-3.5 w-3.5 text-slate-700" />
        <span className="text-[12px] font-bold text-slate-800">
          Storico ipotesi diagnostiche ({items.length})
        </span>
      </div>
      <ol className="relative border-l-2 border-slate-200 ml-2 space-y-2">
        {items.map((h, i) => {
          const meta = stageMeta[h.stage] || stageMeta.working;
          return (
            <li key={i} className="ml-4" data-testid={`hyp-hist-item-${i}`}>
              <span className={`absolute -left-[9px] mt-1 h-3 w-3 rounded-full ${meta.dot} ring-2 ring-white`} />
              <div className={`rounded-lg border-2 p-2 ${meta.cls}`}>
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-wide">{meta.label}</span>
                  <span className="text-[10px] italic">
                    {h.at ? new Date(h.at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                    {h.changed_by ? ` · ${h.changed_by}` : ""}
                  </span>
                </div>
                <div className="text-sm font-bold mt-0.5">{h.diagnosis}</div>
                {typeof h.probability === "number" && (
                  <div className="text-[11px]">Probabilità stimata: {h.probability}%</div>
                )}
                {h.rationale && <div className="text-[11px] italic mt-0.5 opacity-90">{h.rationale}</div>}
                {h.note && <div className="text-[11px] mt-0.5"><b>Nota:</b> {h.note}</div>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function DifferentialDxBox({ items, onDismiss }) {
  const sevCls = {
    high: "border-red-300 bg-red-50",
    moderate: "border-amber-300 bg-amber-50",
    low: "border-emerald-300 bg-emerald-50",
  };
  const barCls = {
    high: "bg-red-500", moderate: "bg-amber-500", low: "bg-emerald-500",
  };
  return (
    <div className="mt-4 rounded-xl border-2 border-fuchsia-200 bg-fuchsia-50/40 p-3"
         data-testid="case-detail-dx-box">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-fuchsia-700" />
          <div className="text-[12px] font-semibold text-fuchsia-800">
            Ipotesi diagnostiche AI ({items.length}) — da validare dal medico
          </div>
        </div>
        <button onClick={onDismiss}
                className="text-slate-600 hover:text-slate-900 text-xs font-semibold underline underline-offset-2"
                data-testid="case-detail-dx-dismiss">Nascondi</button>
      </div>
      <ol className="space-y-2" data-testid="case-detail-dx-list">
        {items.map((d, i) => (
          <li key={i} className={`rounded-lg border-2 p-3 ${sevCls[d.severity] || sevCls.moderate}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-500 font-mono">#{i+1}</span>
                  <span className="font-bold text-slate-900 text-sm">{d.diagnosis}</span>
                </div>
                <div className="mt-1.5 text-[12px] text-slate-700">{d.rationale}</div>
                {d.next_steps && (
                  <div className="mt-1.5 text-[11px] text-slate-600">
                    <span className="font-semibold">Prossimi passi:</span> {d.next_steps}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end flex-shrink-0 w-24">
                <div className="text-lg font-bold text-slate-900 leading-none">{d.probability}%</div>
                <div className="mt-1 w-full h-3 rounded-full bg-white overflow-hidden ring-1 ring-slate-300">
                  <div className={`h-full ${barCls[d.severity] || barCls.moderate}`}
                       style={{ width: `${Math.max(3, d.probability)}%` }} />
                </div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                  {d.severity === "high" ? "Severità alta" : d.severity === "low" ? "Severità bassa" : "Severità media"}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-2 text-[10px] italic text-slate-500">
        Assistenza AI a supporto del ragionamento clinico. Non sostituisce la valutazione professionale.
      </div>
    </div>
  );
}
